/**
 * "Propose option / licence / assignment record" — the organisation's side.
 *
 * The C1 figure's link says it "Opens a picker limited to granted projects,
 * with reference and note fields", and that is exactly what this is. Limited to
 * granted projects is not a nicety: the API refuses a project the inventor has
 * not shared with this portfolio, so offering one would be offering a button
 * that cannot work.
 *
 * It is built here rather than in components/aggregator/** because that
 * directory belongs to another lane. The section beside it mounts this dialog,
 * so the aggregator lane mounts ONE component and gets the whole flow.
 *
 * WHAT IT DOES NOT DO. Proposing gives this organisation nothing. It cannot see
 * one byte more of the invention than the inventor already granted it, and
 * acknowledgement will not change that either. The sentence above the button
 * says so, and it is the same sentence the inventor is shown when they
 * acknowledge — both sides read one definition of what they are doing.
 */
import { useState } from 'react'
import { PROPOSE_CONFIRMATION, kindLabel } from './ipModel'
import type { IpArrangementKind, IpArrangementProposal } from '../../types/ipArrangements'

const KINDS: IpArrangementKind[] = ['option', 'licence', 'assignment']
const MAX_REFERENCE = 300

export interface IpArrangementProposeDialogProps {
  /** Only the projects this portfolio has been granted — the API refuses the rest. */
  grantedProjects: { id: string; name: string }[]
  onSubmit: (proposal: IpArrangementProposal) => Promise<void>
  onClose: () => void
}

export default function IpArrangementProposeDialog({
  grantedProjects, onSubmit, onClose,
}: IpArrangementProposeDialogProps) {
  const [projectId, setProjectId] = useState(grantedProjects[0]?.id ?? '')
  const [kind, setKind] = useState<IpArrangementKind>('option')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const ready = Boolean(projectId) && reference.trim().length > 0

  const submit = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await onSubmit({
        project_id: projectId, kind, reference: reference.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      onClose()
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      // The figure: "The same kind over a live record is refused (409) and the
      // button says so; a different kind supersedes it." So say it, in words.
      setProblem(
        status === 409
          ? `A ${kind} is already on record for this project. Propose a different kind to supersede it — an option becomes a licence, a licence becomes an assignment.`
          : status === 403
            ? 'That was refused. Either this project has not been shared with this portfolio, or your role cannot put an arrangement on record.'
            : 'That did not go through, and nothing was recorded.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-part="IpArrangementProposeDialog"
      role="dialog"
      aria-label="Propose an IP arrangement"
      className="mt-2 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2.5"
    >
      {grantedProjects.length === 0 ? (
        <p className="text-[12px] leading-relaxed text-slate-600">
          This inventor has not shared a project with you yet, so there is
          nothing to put an arrangement against. Sharing is theirs to give,
          project by project.
        </p>
      ) : (
        <>
          <label className="block text-[11px] font-semibold text-slate-700" htmlFor="ip-project">
            Project
          </label>
          <select
            id="ip-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[12px] text-slate-800"
          >
            {grantedProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <fieldset className="mt-2">
            <legend className="text-[11px] font-semibold text-slate-700">Kind</legend>
            <div className="mt-0.5 flex flex-wrap gap-3">
              {KINDS.map((k) => (
                <label key={k} className="flex items-center gap-1.5 text-[12px] text-slate-700">
                  <input
                    type="radio"
                    name="ip-kind"
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  {kindLabel(k)}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="mt-2 block text-[11px] font-semibold text-slate-700" htmlFor="ip-reference">
            Reference — what signed agreement this points at
          </label>
          <input
            id="ip-reference"
            value={reference}
            maxLength={MAX_REFERENCE}
            onChange={(e) => setReference(e.target.value)}
            placeholder="SAFE side-letter 12 Mar 2026"
            className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[12px] text-slate-800"
          />

          <label className="mt-2 block text-[11px] font-semibold text-slate-700" htmlFor="ip-note">
            Note (optional)
          </label>
          <textarea
            id="ip-note"
            value={note}
            rows={2}
            onChange={(e) => setNote(e.target.value)}
            className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[12px] text-slate-800"
          />

          <p className="mt-2 text-[11px] leading-relaxed text-slate-600">
            The inventor confirms: “{PROPOSE_CONFIRMATION}” Every owner must
            acknowledge before it reads as binding, and any one of them may
            decline or dispute, which ends it for all.
          </p>

          {problem && <p className="mt-1.5 text-[11.5px] text-rose-600">{problem}</p>}

          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              disabled={!ready || busy}
              onClick={submit}
              className="rounded bg-iflow-600 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-iflow-700 disabled:opacity-60"
            >
              {busy ? 'Recording…' : 'Put it on record'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
